import { useEffect, useRef, useState } from 'react';
import { fetchProfileById } from '../services/onboardingService';
import { mapApiProfileToPermissionDraft } from '../services/profilePermissionsMappers';

function draftHasClonedPermissions(objectPermissions) {
  return objectPermissions.some((p) => p.content_type != null);
}

export function useCloneProfilePermissions(cloneFromProfileId, setDraft, objectPermissions) {
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const lastFetchedId = useRef(null);

  useEffect(() => {
    if (!cloneFromProfileId) {
      setPermissionsLoading(false);
      setPermissionsError(null);
      setPermissionsLoaded(false);
      lastFetchedId.current = null;
      return;
    }

    const idKey = String(cloneFromProfileId);

    if (
      draftHasClonedPermissions(objectPermissions) &&
      (lastFetchedId.current === null || lastFetchedId.current === idKey)
    ) {
      lastFetchedId.current = idKey;
      setPermissionsLoaded(true);
      setPermissionsLoading(false);
      setPermissionsError(null);
      return;
    }

    if (lastFetchedId.current === idKey) {
      return;
    }

    let cancelled = false;
    const load = async () => {
      setPermissionsLoading(true);
      setPermissionsError(null);
      setPermissionsLoaded(false);

      const res = await fetchProfileById(cloneFromProfileId);
      if (cancelled) return;

      if (!res.ok || !res.data) {
        setPermissionsError('Could not load permissions for the selected profile.');
        setPermissionsLoading(false);
        return;
      }

      const mapped = mapApiProfileToPermissionDraft(res.data);
      if (!mapped.objectPermissions.length && !mapped.generalPermissions.length) {
        setPermissionsError('Selected profile has no permissions to clone.');
        setPermissionsLoading(false);
        return;
      }

      setDraft((prev) => ({
        ...prev,
        objectPermissions: mapped.objectPermissions,
        generalPermissions: mapped.generalPermissions,
      }));

      lastFetchedId.current = idKey;
      setPermissionsLoaded(true);
      setPermissionsLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [cloneFromProfileId, setDraft, objectPermissions]);

  return { permissionsLoading, permissionsError, permissionsLoaded };
}
